import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ChatProvider } from "@/contexts/ChatContext";
import ChatPanel from "@/components/chat/ChatPanel";
import ChatBubble from "@/components/chat/ChatBubble";
import WhatsAppBubble from "@/components/WhatsAppBubble";
import ForcePasswordChangeModal from "@/components/modals/ForcePasswordChangeModal";
import AccountPendingOverlay from "@/components/AccountPendingOverlay";
import Navbar from "@/components/Navbar";
import { useProfileReady } from "@/hooks/useProfileReady";
import Index from "./pages/Index";

const AuthPage = lazy(() => import("./pages/AuthPage"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const ElegirTipo = lazy(() => import("./pages/ElegirTipo"));
const CompletarPerfil = lazy(() => import("./pages/CompletarPerfil"));
const BuscarCompawdres = lazy(() => import("./pages/BuscarCompawdres"));
const PerfilCompawdre = lazy(() => import("./pages/PerfilCompawdre"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const DashboardHumano = lazy(() => import("./pages/DashboardHumano"));
const DashboardHumanoPerfil = lazy(() => import("./pages/DashboardHumanoPerfil"));
const DashboardHumanoMascotas = lazy(() => import("./pages/DashboardHumanoMascotas"));
const DashboardCompawdre = lazy(() => import("./pages/DashboardCompawdre"));
const DashboardCompawdrePerfil = lazy(() => import("./pages/DashboardCompawdrePerfil"));
const Favoritos = lazy(() => import("./pages/Favoritos"));
const Mensajes = lazy(() => import("./pages/Mensajes"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const AdminUsuarioEditar = lazy(() => import("./pages/AdminUsuarioEditar"));
const MiPerfilCompawdre = lazy(() => import("./pages/MiPerfilCompawdre"));
const MisResenas = lazy(() => import("./pages/MisResenas"));
const TerminosPage = lazy(() => import("./pages/TerminosPage"));
const PrivacidadPage = lazy(() => import("./pages/PrivacidadPage"));
const ContactoPage = lazy(() => import("./pages/ContactoPage"));
const SobreNosotrosPage = lazy(() => import("./pages/SobreNosotrosPage"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ConfirmarSalida = lazy(() => import("./pages/ConfirmarSalida"));
const PagoExitoso = lazy(() => import("./pages/PagoExitoso"));
const NotFound = lazy(() => import("./pages/NotFound"));
const RecruitmentPage = lazy(() => import("./features/recruitment/pages/RecruitmentPage"));
const ApplyPage = lazy(() => import("./features/recruitment/apply/pages/ApplyPage"));
const DocumentsPortalPage = lazy(() => import("./features/recruitment/documents/pages/DocumentsPortalPage"));
const ResenaPublica = lazy(() => import("./pages/ResenaPublica"));

const queryClient = new QueryClient();

const AUTH_GATE_PATHS = ["/elegir-tipo", "/completar-perfil", "/auth/callback", "/forgot-password", "/reset-password", "/confirmar-salida"];

const AuthGate = ({ children }: { children: React.ReactNode }) => {
  const { user, profile, isAdmin, loading: authLoading } = useAuth();
  const { hasType, isComplete, loading: profileLoading } = useProfileReady();
  const location = useLocation();
  const hasRendered = useRef(false);

  if (authLoading || profileLoading) {
    if (!hasRendered.current) return null;
    return <>{children}</>;
  }
  hasRendered.current = true;

  if (!user || !profile) return <>{children}</>;
  if (isAdmin) return <>{children}</>;
  if (AUTH_GATE_PATHS.includes(location.pathname)) return <>{children}</>;

  if (!hasType) return <Navigate to="/elegir-tipo" replace />;
  if (!isComplete) return <Navigate to="/completar-perfil" replace />;

  return <>{children}</>;
};

const NO_NAVBAR_PATHS = ["/auth/callback", "/dashboard", "/aplicar"];
const NO_NAVBAR_PREFIXES = ["/documentos/", "/resena"];

const isApplyDomain =
  typeof window !== "undefined" &&
  (window.location.hostname === "aplicar.somoscompaws.com" ||
    window.location.hostname.startsWith("aplicar."));

const AppContent = () => {
  const { mustResetPassword, clearMustResetPassword, user, accountStatus, emailConfirmed, isAdmin, profile } = useAuth();
  const { pathname } = useLocation();
  const showNavbar = !NO_NAVBAR_PATHS.includes(pathname) && !NO_NAVBAR_PREFIXES.some(p => pathname.startsWith(p)) && !isApplyDomain;

  return (
    <>
      {showNavbar && <Navbar />}
      <AuthGate>
        <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={isApplyDomain ? <ApplyPage /> : <Index />} />
          <Route path="/registro" element={<AuthPage />} />
          <Route path="/aplicar" element={<ApplyPage />} />
          <Route path="/documentos/:token" element={<DocumentsPortalPage />} />
          <Route path="/resena/:token" element={<ResenaPublica />} />
          <Route path="/resena" element={<ResenaPublica />} />
          <Route path="/login" element={<AuthPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/elegir-tipo" element={<ElegirTipo />} />
          <Route path="/completar-perfil" element={<CompletarPerfil />} />
          <Route path="/buscar" element={<BuscarCompawdres />} />
          <Route path="/servicios/:slug" element={<BuscarCompawdres />} />
          <Route path="/compawdre/:id" element={<PerfilCompawdre />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/dashboard-humano" element={<DashboardHumano />} />
          <Route path="/dashboard-humano/perfil" element={<DashboardHumanoPerfil />} />
          <Route path="/dashboard-humano/mascotas" element={<DashboardHumanoMascotas />} />
          <Route path="/dashboard-compawdre" element={<DashboardCompawdre />} />
          <Route path="/dashboard-compawdre/perfil" element={<DashboardCompawdrePerfil />} />
          <Route path="/favoritos" element={<Favoritos />} />
          <Route path="/mensajes-legacy" element={<Mensajes />} />
          <Route path="/mi-perfil" element={<MiPerfilCompawdre />} />
          <Route path="/mis-resenas" element={<MisResenas />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/admin/reclutamiento" element={<RecruitmentPage />} />
          <Route path="/admin/usuario/:id" element={<AdminUsuarioEditar />} />
          <Route path="/admin/:tab" element={<AdminPanel />} />
          <Route path="/terminos" element={<TerminosPage />} />
          <Route path="/privacidad" element={<PrivacidadPage />} />
          <Route path="/contacto" element={<ContactoPage />} />
          <Route path="/sobre-nosotros" element={<SobreNosotrosPage />} />
          <Route path="/confirmar-salida" element={<ConfirmarSalida />} />
          <Route path="/pago-exitoso" element={<PagoExitoso />} />
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </AuthGate>
      {user && mustResetPassword && (
        <ForcePasswordChangeModal
          open={true}
          onPasswordChanged={clearMustResetPassword}
        />
      )}
      {user && !isAdmin && !emailConfirmed && <AccountPendingOverlay />}
      <ChatPanel />
      <ChatBubble />
      {!pathname.startsWith("/resena") && <WhatsAppBubble />}
    </>
  );
};

export const AppShell = ({ router }: { router: ReactNode }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        {mounted && <Toaster />}
        {mounted && <Sonner />}
        {router}
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export const AppRoutes = () => (
  <AuthProvider>
    <ChatProvider>
      <AppContent />
    </ChatProvider>
  </AuthProvider>
);

const App = () => (
  <AppShell
    router={
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    }
  />
);

export default App;
